import styled from 'styled-components';

import { PADDING_BY_SIZE } from '@front/common/src/components/selectable-card/constants';
import type { WellnessBasketPrices } from '@front/common/src/types/commons';
import colors from '@front/common/src/utils/colors';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { formatPercentage, formatPrice } from '@front/common/src/utils/formatting';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import type { MembershipOptIn } from '@front/customer/src/modals/membership-paywall/utils';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';

import SelectablePriceCard, { type SelectablePriceCardProps } from '../selectable-price-card';

const t = translate('modals.membership_paywall.price_cards.member');

const StyledCard = styled(SelectablePriceCard)`
  height: 98px;

  /*
   The full label adds extra height, but it uses absolute position.
   Therefore we need to add extra padding on top so that the rest of the card is still correctly
   horizontally centred.
  */
  padding-top: calc(${PADDING_BY_SIZE.md}px + 13px);
`;

const StyledPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

type SelectableWellnessMemberPriceCardProps = Pick<SelectablePriceCardProps, 'onPress'> & {
  numberOfSelectedPrestations: number;
  prices: WellnessBasketPrices;
  optInChoice?: MembershipOptIn;
};

const SelectableWellnessMemberPriceCard = ({
  numberOfSelectedPrestations,
  onPress,
  prices,
  optInChoice,
}: SelectableWellnessMemberPriceCardProps) => {
  const {
    totalPrice,
    membership: { reduction: membershipReduction, totalPrice: membershipTotalPrice },
  } = prices;

  const showPrices = numberOfSelectedPrestations > 0;
  const formattedReduction = showPrices
    ? formatPrice(membershipReduction)
    : formatPercentage(MEMBERSHIP_CONFIG_BY_COUNTRY[getCurrentCountry()].wellnessReductionsPercentage);

  return (
    <StyledCard
      isSelected={optInChoice === 'membership'}
      onPress={onPress}
      title={t('.pay_with_club')}
      label={t('.label', { memberReduction: formattedReduction })}
      hasFullWidthLabel
      value='membership'
    >
      {showPrices && (
        <StyledPriceWrapper>
          <Text strikedOut>{formatPrice(totalPrice)}</Text>
          <Text.MDBold color={colors.pink120}>{formatPrice(membershipTotalPrice)}</Text.MDBold>
        </StyledPriceWrapper>
      )}
    </StyledCard>
  );
};

export default SelectableWellnessMemberPriceCard;
